import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorText } from "../../lib/api";
import { SlotGrid, humanDate, useSlots } from "../forms/SlotPicker";

export const STATUSES = [["nouveau", "Nouveau"], ["en-attente", "En attente"], ["confirme", "Confirmé"], ["refuse", "Refusé"], ["annule", "Annulé"], ["contacte", "Contacté"], ["rdv-fixe", "RDV fixé"], ["termine", "Terminé"]];
export const statusLabel = (s) => STATUSES.find(([v]) => v === s)?.[1] || s;
const FIELDS = [["prenom", "Prénom"], ["telephone", "Téléphone"], ["email", "Email"], ["ville", "Ville"], ["adresse", "Adresse"], ["service", "Service"], ["type_vehicule", "Véhicule"], ["marque", "Marque"], ["modele", "Modèle"], ["immatriculation", "Immatriculation"], ["creneau", "Créneau"], ["message", "Message"]];
const EVENTS = { confirme: "Confirmation", refuse: "Refus", annule: "Annulation", modifie: "Modification", rappel: "Rappel la veille" };
const SENT = { sent: "envoyé", failed: "échec", skipped: "non envoyé (pas d'email client)", pending: "en cours" };

function Reschedule({ lead, onDone }) {
  const [date, setDate] = useState(lead.date_souhaitee || "");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const { loading, slots, closed } = useSlots(date, { admin: true, exclude: lead.id });
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/leads/${lead.id}`, { date_souhaitee: date, creneau: time });
      toast.success(r.data?.notified ? "Rendez-vous modifié — le client est prévenu par email." : "Rendez-vous modifié.");
      onDone();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 rounded-2xl bg-ag-mist p-4" data-testid="lead-reschedule">
      <p className="mb-3 font-semibold text-ag-ink">Modifier la date / l'horaire</p>
      <input type="date" className="field mb-3 max-w-[220px]" value={date} onChange={(e) => { setDate(e.target.value); setTime(""); }} data-testid="reschedule-date" />
      {loading && <p className="text-sm text-slate-500">Chargement…</p>}
      {!loading && date && slots.length === 0 && <p className="text-sm text-slate-600">{closed ? "Centre fermé ce jour-là." : "Aucun créneau libre ce jour-là."}</p>}
      {!loading && slots.length > 0 && <SlotGrid slots={slots} value={time} onChange={setTime} testid="reschedule-slot" />}
      <button disabled={!time || busy} onClick={save} className="btn btn-blue mt-4" data-testid="reschedule-save">{busy ? "Enregistrement…" : "Enregistrer le nouveau créneau"}</button>
    </div>
  );
}

export function LeadDetail({ id, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    api.get(`/admin/leads/${id}`).then((r) => { if (alive) setLead(r.data); }).catch((e) => toast.error(errorText(e)));
    return () => { alive = false; };
  }, [id, tick]);
  useEffect(() => { if (!id) setLead(null); }, [id]);
  const photos = [...(lead?.photos_parebrise || []), ...(lead?.photos_optique || [])];
  const isRdv = lead?.type === "rendez-vous" && /^\d{2}:\d{2}$/.test(lead?.creneau || "");
  const es = lead?.email_status;
  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader><DialogTitle>{lead ? `${lead.nom} — ${lead.type}` : "Chargement…"}</DialogTitle></DialogHeader>
        {lead && isRdv && <p className="rounded-xl bg-ag-navy px-4 py-3 font-semibold text-white first-letter:uppercase" data-testid="lead-detail-slot">{humanDate(lead.date_souhaitee, lead.creneau)} · {statusLabel(lead.status)}</p>}
        {lead && (
          <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
            {FIELDS.filter(([k]) => lead[k] && !(isRdv && k === "creneau")).map(([k, l]) => (<Fragment key={k}><dt className="font-semibold text-slate-500">{l}</dt><dd className="whitespace-pre-wrap text-ag-ink">{lead[k]}</dd></Fragment>))}
          </dl>
        )}
        {photos.length > 0 && <div className="mt-4 grid grid-cols-3 gap-2">{photos.map((p, i) => <a key={i} href={p} target="_blank" rel="noreferrer"><img src={p} alt={`Photo ${i + 1}`} className="aspect-square w-full rounded-lg object-cover" /></a>)}</div>}
        {lead && isRdv && <Reschedule key={`${lead.date_souhaitee}-${lead.creneau}`} lead={lead} onDone={() => { setTick((n) => n + 1); onChanged(); }} />}
        {lead && (
          <div className="mt-5 space-y-1 border-t border-slate-200 pt-4 text-xs text-slate-600" data-testid="lead-email-log">
            <p className="font-semibold text-ag-ink">Emails</p>
            <p>Notification centre : {SENT[es?.owner] || "—"}{es?.owner_error ? ` (${es.owner_error})` : ""}</p>
            <p>Accusé de réception client : {SENT[es?.customer] || "—"}{es?.customer_error ? ` (${es.customer_error})` : ""}</p>
            {(lead.email_log || []).map((e, i) => <p key={i}>{EVENTS[e.event] || e.event} → client : {e.ok ? "envoyé" : `échec (${e.error || "erreur"})`} · {new Date(e.at).toLocaleString("fr-FR")}</p>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
